// Rutas del panel. La app standalone SOLO enruta, autentica y monta los
// componentes de @victorix/chat-ai-ui (C7-D1): la lógica vive en el paquete.
import { BrowserRouter, Route, Routes, useNavigate, useSearchParams } from "react-router-dom";
import {
  Campanias, Contactos, Embudo, Equipo, Inbox, Metricas,
  ProbarBot, Seguimientos, Vacio,
  type ContextoPanel,
} from "@victorix/chat-ai-ui";
import { ProveedorSesion, useSesion } from "./lib/auth.tsx";
import { sb, configurado } from "./lib/supabase.ts";
import Shell from "./Shell.tsx";
import Login from "./paginas/Login.tsx";
import Invitacion from "./paginas/Invitacion.tsx";
import NuevaContrasena from "./paginas/NuevaContrasena.tsx";
import RecuperarClave from "./paginas/RecuperarClave.tsx";
import SinAcceso from "./paginas/SinAcceso.tsx";
import { rutasDelEntrenamiento } from "./paginas/Entrenamiento.tsx";

/**
 * ⭐⭐ DÓNDE VIVE LA API DEL SIMULADOR (T-SIM-16, 31-ago-2026).
 *
 * 🔴 En desarrollo el panel y la API comparten origen y `/api/simular` lo
 * reescribe el proxy de Vite. **Publicado NO**: `chat.victorix-ai.com` es
 * Cloudflare Pages, estático, y ese mismo `/api/simular` lo contestaba
 * Cloudflare con un 405 de cuerpo vacío — por eso «Probar bot» nunca funcionó
 * fuera del Mac. En producción esta variable apunta al dominio propio del
 * simulador (`despliegue/`), y el CORS del servidor solo admite este panel.
 */
const URL_SIMULADOR = import.meta.env.VITE_SIMULADOR_URL ?? "/api/simular";

/**
 * A dónde lleva «Abrir el Catálogo del ERP» desde la pantalla `/catalogo`
 * (T-CAT-13). La librería no enruta ni conoce dominios (C7-D1), así que la URL
 * la pone el anfitrión.
 *
 * ⛔ SIN DEFECTO INVENTADO. Si la variable no está, la pantalla dice dónde se
 * administra el catálogo y NO pinta enlace: mandar al cliente a un 404 es peor
 * que no darle el atajo.
 */
const URL_CATALOGO_ERP = import.meta.env.VITE_URL_CATALOGO_ERP as string | undefined;

// El puente "Probar bot" → inbox. La librería no enruta (C7-D1): expone un
// callback y la app decide la URL. `?c=<id>` es además un enlace compartible.
//
// ⛔ El `tenantId` que se manda es el ACTIVO DEL SELECTOR, y el servidor NO se
// lo cree: comprueba contra la base, con el token del propio usuario, que sea
// miembro activo de ese negocio. Si no lo fuera, aquí no habría nada que hacer.
function ProbarBotEnrutado() {
  const navegar = useNavigate();
  const { tenantId, obtenerToken } = useSesion();
  return (
    <ProbarBot
      apiUrl={URL_SIMULADOR}
      tenantId={tenantId}
      obtenerToken={obtenerToken}
      onVerEnInbox={(id) => navegar(`/?c=${encodeURIComponent(id)}`)}
    />
  );
}

function InboxEnrutado({ ctx }: { ctx: ContextoPanel }) {
  const [params] = useSearchParams();
  return <Inbox ctx={ctx} convInicial={params.get("c")} />;
}

function Panel() {
  const { cargando, usuarioId, tenantId, rol } = useSesion();

  if (cargando) {
    return (
      <div className="h-pantalla grid place-items-center">
        <div className="skeleton h-6 w-40 rounded" />
      </div>
    );
  }
  if (!usuarioId) return <Login />;

  // Sin membresía no se cae a un renglón gris: es la PRIMERA pantalla de quien
  // llega, y la primera impresión no se corrige después. Ver `SinAcceso.tsx`.
  if (!tenantId) return <SinAcceso />;

  const ctx: ContextoPanel = { sb, tenantId, usuarioId, rol };

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<InboxEnrutado ctx={ctx} />} />
        <Route path="seguimientos" element={<Seguimientos ctx={ctx} />} />
        <Route path="contactos" element={<Contactos ctx={ctx} />} />
        <Route path="embudo" element={<Embudo ctx={ctx} />} />
        <Route path="metricas" element={<Metricas ctx={ctx} />} />
        <Route path="campanias" element={<Campanias ctx={ctx} />} />
        <Route path="probar" element={<ProbarBotEnrutado />} />
        <Route path="equipo" element={<Equipo ctx={ctx} />} />
        {/* T-ENT-5 · el entrenamiento es UN destino con pestañas; las cinco URL
            viejas redirigen a la suya (la lista vive en paginas/Entrenamiento.tsx). */}
        {rutasDelEntrenamiento(ctx, URL_CATALOGO_ERP)}
        <Route path="*" element={<Vacio titulo="Esa pantalla no existe">Usa el menú para volver.</Vacio>} />
      </Route>
    </Routes>
  );
}

export default function App() {
  if (!configurado) {
    return (
      <div className="h-pantalla grid place-items-center px-6 text-center">
        <div>
          <p className="font-medium">Falta configurar la base</p>
          <p className="text-sm text-tinta-2 mt-1">
            Define <code className="bg-tenue px-1.5 py-0.5 rounded">VITE_SUPABASE_URL</code> y{" "}
            <code className="bg-tenue px-1.5 py-0.5 rounded">VITE_SUPABASE_ANON_KEY</code> en <code>web/.env.local</code>.
          </p>
        </div>
      </div>
    );
  }
  return (
    <BrowserRouter>
      <ProveedorSesion>
        {/* ⭐ LAS RUTAS PÚBLICAS VAN ARRIBA DEL GUARD DE SESIÓN, no dentro de
            `Panel`. Hasta hoy `Panel` cortaba con `if (!usuarioId) return
            <Login/>` ANTES de montar ninguna ruta: `/invitacion/<token>` mostraba
            el login y el vale se perdía. Y quien acababa de crear su cuenta —sin
            membresía todavía— caía en el «tu cuenta no está en ningún negocio»,
            que es justo lo contrario de lo que acababa de pasar.
            Estas tres pantallas tienen que verse SIN sesión; la seguridad no la
            da esconderlas, la da la base (el vale y el cotejo del correo). */}
        <Routes>
          <Route path="/invitacion/:token" element={<Invitacion />} />
          <Route path="/nueva-contrasena" element={<NuevaContrasena />} />
          <Route path="/recuperar" element={<RecuperarClave />} />
          <Route path="/*" element={<Panel />} />
        </Routes>
      </ProveedorSesion>
    </BrowserRouter>
  );
}
